/**
 * collection.js — behaviour for the collection / search / list-collections
 * lane. Loaded (deferred) from sections/main-collection.liquid,
 * sections/main-search.liquid.
 *
 * Custom elements:
 *
 *   <kosha-quick-add>
 *     Wraps the card's quick-add control. Three markup shapes, one behaviour,
 *     and — since the owner's "it should just be a plus" call — one trigger:
 *     a 38px cream "+" circle on every buyable card, whatever it opens.
 *       - single variant : <form> with one submit button carrying name="id"
 *       - one option     : trigger button + anchored <form> of size pills,
 *                          every pill a submit button carrying name="id"
 *       - two+ options   : trigger LINK to the PDP + anchored <form> of one
 *         (sets)           legend + pill row per option group, plus an Add
 *                          button whose name="id" value this file writes from
 *                          the exact chosen combination. The variant matrix
 *                          ships in a <script type="application/json"
 *                          data-quick-variants> inside the panel, so the pills
 *                          strike correctly on the first frame with no fetch.
 *     The first two are real POSTs to /cart/add, so they work with JS off; the
 *     set trigger is a real link, so with JS off it lands on the product page.
 *     With JS all three are intercepted and turned into a guarded AJAX add.
 *     After a successful add it rewrites every [data-cart-count] (textContent
 *     + data-empty) and dispatches a bubbling, composed `kosha:cart:added`
 *     with `detail.source === 'card'`.
 *     A failed add closes the size panel, turns the control into a visible
 *     "Try again" button and writes into a `role="status"` element the
 *     element creates for itself.
 *
 * Progressive enhancements (no custom element needed):
 *   - [data-facet]      : filter disclosures — one open at a time, Escape and
 *                         outside-click close. Without JS they are plain
 *                         <details>, and every filter value is a real link.
 *   - [data-sort-form]  : hides its own no-JS "Apply" button and applies on
 *                         change.
 *   - .pcard__dot       : in-place colour swap. Plain left click rewrites the
 *                         card from /products/<handle>.js instead of
 *                         navigating; modifier clicks are left alone.
 *   - [data-collection-root] : filter / sort / pagination links and the sort
 *                         select update the page through the Section
 *                         Rendering API and history.pushState, with no
 *                         document navigation.
 *
 * One IIFE, no modules, no build step — same shape as assets/product.js.
 *
 * SHARED HELPERS: `cartRoute`, `updateCartCount`, `formatMoney`, `escapeHtml`
 * and `reducedMotion` are one-line delegates to `window.kosha`
 * (assets/global.js, loaded from <head> so it always runs first). See the
 * SHARED HELPERS block there.
 */

(function () {
  'use strict';

  /* ==========================================================================
     Shared helpers  →  window.kosha (assets/global.js owns the one copy).
     Named locals so the call sites below read unchanged; resolved at call
     time, so nothing here depends on evaluation order. `seedCartCount` is
     absent: global.js runs the identical seed on every page.
     ========================================================================== */

  function cartRoute(path) {
    return window.kosha.cartRoute(path);
  }

  /* Rewrites every [data-cart-count] the header rendered, data-empty included
     (the header prints the raw "0"; CSS hides that state). */
  function updateCartCount(count) {
    return window.kosha.updateCartCount(count);
  }

  function escapeHtml(value) {
    return window.kosha.escapeHtml(value);
  }

  function reducedMotion() {
    return window.kosha.prefersReducedMotion();
  }

  /* /products/<handle>.js returns integer cents and no format string; the
     format is in the DOM on every page (the drawer's `data-money-format`),
     which is where `shopMoneyFormat()` reads it. */
  function formatMoney(cents) {
    return window.kosha.formatMoney(cents, window.kosha.shopMoneyFormat());
  }

  /* A plain left click — the only kind either enhancement is allowed to
     swallow. Cmd/ctrl (new tab), shift (new window), alt (download) and
     middle-click (which fires `auxclick`, not `click`) all fall through to the
     browser, so every dot and every filter value keeps working as a link. */
  function isPlainClick(event) {
    return (
      !event.defaultPrevented &&
      event.button === 0 &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey
    );
  }

  function closestFrom(target, selector) {
    return target && target.closest ? target.closest(selector) : null;
  }

  /* ==========================================================================
     <kosha-quick-add>
     ========================================================================== */

  /* Only one size panel is ever open. Module-scoped so a second card closes
     the first without either of them knowing about the other. */
  var openQuickAdd = null;

  var ADD_FAILED = 'Couldn’t add that. Try again.';

  /* `[[id, ["S","XS"], 1], …]` → `[{ id, options, available }, …]`. Compact on
     the wire because it rides on every set card in the grid; the shape is
     positional, so it does not care how many option groups a product has. */
  function readVariantMatrix(node) {
    if (!node) return [];
    var raw;
    try {
      raw = JSON.parse(node.textContent || '[]');
    } catch (error) {
      return [];
    }
    if (!Array.isArray(raw)) return [];
    return raw.map(function (entry) {
      return {
        id: entry[0],
        options: Array.isArray(entry[1]) ? entry[1] : [],
        available: entry[2] === 1,
      };
    });
  }

  class KoshaQuickAdd extends HTMLElement {
    constructor() {
      super();
      this.pending = false;
      this.resetTimer = null;
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleTrigger = this.handleTrigger.bind(this);
      this.handleClose = this.handleClose.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleOption = this.handleOption.bind(this);
      this.groups = [];
      this.variants = [];
    }

    connectedCallback() {
      this.form = this.querySelector('[data-quick-form]');
      this.trigger = this.querySelector('[data-quick-trigger]');
      this.panel = this.querySelector('[data-quick-panel]');
      this.closeButton = this.querySelector('[data-quick-close]');
      this.label = this.querySelector('[data-quick-label]');
      this.defaultLabel = this.label ? this.label.textContent : '';

      /* Built here rather than in the Liquid for two reasons: it costs zero
         bytes on a 24-card page, and a live region has to already be in the
         accessibility tree when its text changes or the change is not
         announced — creating it at error time would announce nothing.

         Adopt an existing node before making one. `restoreCard()` puts back an
         innerHTML snapshot that was taken AFTER this element first ran, so the
         markup it re-inserts already contains an alert — but it re-inserts it
         as a brand new element instance, whose `this.alert` is undefined. Left
         to itself that appended a second live region on every colour-dot
         round trip. Reusing the parsed one keeps it at exactly one. */
      this.alert = this.querySelector('.pcard__alert');
      if (!this.alert) {
        this.alert = document.createElement('p');
        this.alert.className = 'pcard__alert';
        this.alert.setAttribute('role', 'status');
        this.appendChild(this.alert);
      }

      if (this.form) this.form.addEventListener('submit', this.handleSubmit);
      if (this.trigger) this.trigger.addEventListener('click', this.handleTrigger);
      if (this.closeButton) this.closeButton.addEventListener('click', this.handleClose);
      this.addEventListener('keydown', this.handleKeydown);

      this.setupOptionGroups();
    }

    /* ------------------------------------------------ two-group (set) mode */

    /* No-op on the single-variant and one-option cards — they have no
       `[data-quick-group]`, so `this.groups` stays empty and every branch
       below short-circuits. */
    setupOptionGroups() {
      this.groups = Array.prototype.slice.call(
        this.querySelectorAll('[data-quick-group]')
      );
      if (!this.groups.length) return;

      this.variants = readVariantMatrix(this.querySelector('[data-quick-variants]'));
      this.addButton = this.querySelector('[data-quick-submit]');
      this.addLabel = this.querySelector('[data-quick-add-label]');
      this.addEventListener('click', this.handleOption);
      this.refreshOptions();
      this.advance(false);
    }

    handleOption(event) {
      var button = closestFrom(event.target, '[data-quick-option]');
      if (!button || !this.contains(button)) return;
      var group = button.closest('[data-quick-group]');
      if (!group) return;

      /* Re-tapping the chosen value clears it. Without that a shopper who
         picks a top size which rules out every bottom size has no way back:
         struck values stay selectable (same as the PDP) but the group they
         came from would otherwise be locked to one answer. */
      var wasOn = button.getAttribute('aria-pressed') === 'true';
      group.querySelectorAll('[data-quick-option]').forEach(function (other) {
        other.setAttribute('aria-pressed', 'false');
      });
      button.setAttribute('aria-pressed', wasOn ? 'false' : 'true');
      this.refreshOptions();

      /* A multi-group pick NEVER completes the panel — it stays open until
         every group has an answer. So the pick has to hand the shopper to the
         next unanswered group rather than leaving them staring at a row they
         have already dealt with. Same rule the PDP's sticky picker follows.
         Nothing to advance to on a single-group card: there is no
         `[data-quick-group]` there and this whole path never runs. */
      this.advance(true);
    }

    /**
     * Move to the first group that still has no selection: mark it current
     * (CSS lifts it and dims the settled ones) and put focus on its first
     * buyable pill. When every group is answered the emphasis moves to the Add
     * button, which `refreshOptions` has just enabled.
     */
    advance(focus) {
      if (!this.groups.length) return;
      var selection = this.selection();
      var next = -1;
      for (var i = 0; i < selection.length; i += 1) {
        if (selection[i] === null) {
          next = i;
          break;
        }
      }

      this.groups.forEach(function (group, index) {
        group.toggleAttribute('data-current', index === next);
        group.toggleAttribute('data-settled', selection[index] !== null);
      });

      if (!focus) return;

      if (next === -1) {
        if (this.addButton && !this.addButton.disabled) {
          this.addButton.focus({ preventScroll: true });
        }
        return;
      }

      var target =
        this.groups[next].querySelector('[data-quick-option]:not([data-unavailable])') ||
        this.groups[next].querySelector('[data-quick-option]');
      if (target) target.focus({ preventScroll: true });
    }

    /** 159px: four 30px pills, three 3px gaps, 4px of row padding, 12px of
        panel padding and the panel's own 16px inset off the media box. */
    panelFits() {
      var media = this.closest('.pcard__media');
      return !media || media.getBoundingClientRect().width >= 159;
    }

    /** Chosen value per group, `null` where nothing is chosen yet. */
    selection() {
      return this.groups.map(function (group) {
        var on = group.querySelector('[data-quick-option][aria-pressed="true"]');
        return on ? on.value : null;
      });
    }

    /** Exact combination — every group's value has to match, in order. */
    matchVariant(selection) {
      var found = null;
      this.variants.forEach(function (variant) {
        if (found) return;
        if (variant.options.length !== selection.length) return;
        var hit = selection.every(function (value, index) {
          return variant.options[index] === value;
        });
        if (hit) found = variant;
      });
      return found;
    }

    /**
     * The PDP's cross-group narrowing, in a card. A value is reachable when
     * some AVAILABLE variant carries it in this group and agrees with whatever
     * is already chosen in the others — so picking "M" on top strikes every
     * bottom size that has no available M/x combination. Unreachable values
     * are struck and renamed in the a11y tree, never disabled: a disabled
     * button cannot be focused, and the shopper needs a way out.
     */
    refreshOptions() {
      if (!this.groups.length) return;
      var element = this;
      var selection = this.selection();

      this.groups.forEach(function (group, groupIndex) {
        group.querySelectorAll('[data-quick-option]').forEach(function (button) {
          var reachable = element.variants.some(function (variant) {
            if (!variant.available) return false;
            if (variant.options[groupIndex] !== button.value) return false;
            return selection.every(function (value, index) {
              return index === groupIndex || value === null || variant.options[index] === value;
            });
          });

          button.toggleAttribute('data-unavailable', !reachable);
          if (reachable) button.removeAttribute('aria-label');
          else button.setAttribute('aria-label', button.value + ', sold out');
        });
      });

      var complete = selection.every(function (value) {
        return value !== null;
      });
      var variant = complete ? this.matchVariant(selection) : null;
      var buyable = !!(variant && variant.available);

      if (this.addButton) {
        this.addButton.value = buyable ? String(variant.id) : '';
        this.addButton.disabled = !buyable;
      }
      if (this.addLabel) {
        this.addLabel.textContent = !complete
          ? 'Select sizes'
          : buyable
            ? 'Add to cart'
            : 'Sold out';
      }
    }

    disconnectedCallback() {
      if (openQuickAdd === this) openQuickAdd = null;
      if (this.resetTimer) clearTimeout(this.resetTimer);
    }

    /* ------------------------------------------------------------- panel */

    get isOpen() {
      return !!this.panel && !this.panel.hasAttribute('hidden');
    }

    open() {
      if (!this.panel || this.isOpen) return;
      if (openQuickAdd && openQuickAdd !== this) openQuickAdd.close();
      this.panel.removeAttribute('hidden');
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
      openQuickAdd = this;

      /* Sets open on their first unanswered group with the Add button
         disabled, so `advance` places both the emphasis and the focus.
         One-option cards have no `[data-quick-group]`, `advance` returns
         immediately, and focus lands on their first available size pill
         exactly as before. */
      if (this.groups.length) {
        this.advance(true);
        return;
      }

      var first =
        this.panel.querySelector('[data-quick-submit]:not([disabled])') ||
        this.panel.querySelector('[data-quick-close]');
      if (first) first.focus({ preventScroll: true });
    }

    close(returnFocus) {
      if (!this.panel || !this.isOpen) return;
      this.panel.setAttribute('hidden', '');
      if (this.trigger) {
        this.trigger.setAttribute('aria-expanded', 'false');
        if (returnFocus) this.trigger.focus({ preventScroll: true });
      }
      if (openQuickAdd === this) openQuickAdd = null;
    }

    handleTrigger(event) {
      /* On a set the trigger is a real <a> to the PDP. Only the plain left
         click is swallowed — cmd/ctrl/shift-click still open the product page,
         same rule the colour dots follow. */
      if (this.trigger.tagName === 'A' && !isPlainClick(event)) return;

      /* ...and the click is not swallowed at all when the tile is too narrow
         to hold the panel. A two-group panel needs two rows of four 30px pills
         plus its gutters, which is 159px of media box; the collection lane's
         LIST view gives the photograph a `clamp(96px, 26vw, 156px)` column, so
         the panel would open 85px wide and scroll inside a 133px row. The
         trigger is already a real link to the product page, which is the
         better control at that size and is exactly what the previous "Choose
         options" treatment did. Measured rather than keyed to `data-view`, so
         it holds for any narrow context a future lane drops the card into. */
      if (this.groups.length && !this.panelFits()) return;
      event.preventDefault();
      if (this.isOpen) this.close(true);
      else this.open();
    }

    handleClose(event) {
      event.preventDefault();
      this.close(true);
    }

    handleKeydown(event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      if (!this.isOpen) return;
      event.stopPropagation();
      this.close(true);
    }

    /* --------------------------------------------------------------- add */

    handleSubmit(event) {
      /* In-flight guard, set synchronously before anything async can start.
         Every entry point — real click, Enter on a focused pill, a
         programmatic requestSubmit — arrives here, so five rapid taps still
         produce exactly one POST. */
      if (this.pending) {
        event.preventDefault();
        return;
      }

      var submitter = event.submitter;
      var id = submitter && submitter.name === 'id' ? submitter.value : null;
      if (!id) {
        var fallback = this.querySelector('[name="id"]');
        id = fallback ? fallback.value : null;
      }
      /* No id resolvable → let the browser do the native POST rather than
         swallowing the shopper's intent. */
      if (!id) return;

      event.preventDefault();
      this.pending = true;
      this.setState('loading');

      var element = this;
      fetch(cartRoute('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: id, quantity: 1 }),
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw data;
            return data;
          });
        })
        .then(function (item) {
          return fetch(cartRoute('cart.js'), { headers: { Accept: 'application/json' } })
            .then(function (response) {
              return response.json();
            })
            .catch(function () {
              return null;
            })
            .then(function (cart) {
              if (cart) updateCartCount(cart.item_count);
              element.announce(id, item, cart);
            });
        })
        .catch(function (error) {
          /* The size panel is closed on failure for the same reason the
             success path closes it: the panel sits over the trigger, so a
             state left on the trigger underneath it would be invisible on
             exactly the layout (touch) where it is the only feedback. */
          element.close();
          element.setState('error', error && error.description);
          if (element.resetTimer) clearTimeout(element.resetTimer);
          element.resetTimer = setTimeout(function () {
            element.setState('idle');
          }, 4000);
        })
        /* Released when the request settles, not when the "added" label times
           out: a shopper may add a second size immediately, they just cannot
           fire the same one twice from one gesture. */
        .then(function () {
          element.pending = false;
        });
    }

    announce(id, item, cart) {
      var element = this;
      this.close();
      this.setState('added');
      if (this.resetTimer) clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(function () {
        element.setState('idle');
      }, 2400);

      this.dispatchEvent(
        new CustomEvent('kosha:cart:added', {
          bubbles: true,
          composed: true,
          detail: {
            source: 'card',
            handle: this.getAttribute('data-handle') || '',
            variantId: Number(id),
            item: item,
            cart: cart,
          },
        })
      );
    }

    setState(state, message) {
      var button = this.trigger || this.querySelector('[data-quick-submit]');

      /* The live region carries the failure in words. It is the only feedback
         that is guaranteed visible in the compact (hover: none) layout, where
         the button is a 44px circle and its label is clipped to 1px. */
      if (this.alert) {
        this.alert.textContent = state === 'error' ? message || ADD_FAILED : '';
      }

      if (!button) return;

      if (state === 'idle') button.removeAttribute('data-state');
      else button.setAttribute('data-state', state);

      if (!this.label) return;
      if (state === 'added') this.label.textContent = 'Added';
      else if (state === 'error') this.label.textContent = 'Try again';
      else if (state === 'idle') this.label.textContent = this.defaultLabel;
    }
  }

  /* ==========================================================================
     In-place colour swap
     --------------------------------------------------------------------------
     The dots are real <a> links to each sibling's PDP and stay that way — with
     JS off, and for crawlers, nothing about them changed. A plain left click
     is intercepted and the card is rewritten from that sibling's
     /products/<handle>.js instead.

     PAYLOAD: nothing sibling-specific is embedded in the page for this. The
     handle comes out of the dot's own href, the colour name out of its title
     attribute, and the rest out of one lazily fetched, then cached, JSON
     document. Embedding five siblings' images, prices and variant lists into
     24 cards would have added roughly 90 KB of HTML per page; this adds none.

     KNOWN LIMIT: `theme.badge` / `theme.cutline` are metafields and metafields
     are not in the .js endpoint, so a swapped-to sibling shows the factual
     badge (Sold out / N% off) but not a merchandising one, and falls back to
     the dot's colour name for its cutline — which is what the server-rendered
     card does anyway, since theme.cutline is empty catalogue-wide. Clicking
     back to the original dot restores the pre-swap DOM verbatim, badge and
     all, from a snapshot rather than from the network.
     ========================================================================== */

  var CARD_WIDTHS = [200, 300, 400, 600, 800];
  var DEFAULT_CARD_SIZES = '(min-width: 1100px) 300px, (min-width: 750px) 33vw, 50vw';

  var productCache = Object.create(null);
  var cardSnapshots = new WeakMap();
  var cardTokens = new WeakMap();
  var panelSeq = 0;

  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch(cartRoute('products/' + handle + '.js'), {
      headers: { Accept: 'application/json' },
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .catch(function (error) {
        delete productCache[handle];
        throw error;
      });
    return productCache[handle];
  }

  function handleFromHref(href) {
    var match = String(href || '').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  /* Candidate list for a CDN URL that carries no size parameters (the .js
     endpoint returns the original file). Never upscales past the source. */
  function imageSources(src, naturalWidth) {
    var url;
    try {
      url = new URL(src, window.location.href);
    } catch (error) {
      return null;
    }
    var largest = CARD_WIDTHS[CARD_WIDTHS.length - 1];
    var cap = naturalWidth ? Math.min(naturalWidth, largest) : largest;
    var steps = CARD_WIDTHS.filter(function (width) {
      return width < cap;
    });
    steps.push(cap);

    function at(width) {
      var stepUrl = new URL(url.href);
      stepUrl.searchParams.set('width', String(width));
      return stepUrl.href;
    }

    return {
      src: at(cap),
      srcset: steps
        .map(function (width) {
          return at(width) + ' ' + width + 'w';
        })
        .join(', '),
      width: cap,
    };
  }

  function paintImage(img, media, sizes, loading) {
    var sources = imageSources(media.src, media.width);
    if (!sources) return false;
    var ratio = media.width && media.height ? media.height / media.width : 1.25;
    img.setAttribute('src', sources.src);
    img.setAttribute('srcset', sources.srcset);
    img.setAttribute('sizes', sizes);
    img.setAttribute('alt', '');
    img.setAttribute('width', String(sources.width));
    img.setAttribute('height', String(Math.round(sources.width * ratio)));
    img.setAttribute('loading', loading);
    img.setAttribute('decoding', 'async');
    return true;
  }

  function makeCardImage(className) {
    var img = document.createElement('img');
    img.className = 'image__img pcard__img ' + className;
    return img;
  }

  function quickAddMarkup(data, panelId) {
    if (!data.available) return '';
    var action = cartRoute('cart/add');
    var variants = data.variants || [];
    var options = data.options || [];

    if (variants.length === 1) {
      return (
        '<kosha-quick-add class="pcard__quick" data-handle="' +
        escapeHtml(data.handle) +
        '"><form action="' +
        escapeHtml(action) +
        '" method="post" data-quick-form>' +
        '<input type="hidden" name="quantity" value="1">' +
        '<button class="pcard__quick-btn" type="submit" name="id" value="' +
        escapeHtml(variants[0].id) +
        '" data-quick-submit><span data-quick-label>Add</span>' +
        '<span class="visually-hidden">' +
        escapeHtml(data.title) +
        ' to cart</span></button></form></kosha-quick-add>'
      );
    }

    if (options.length === 1) {
      var pills = variants
        .map(function (variant) {
          return (
            '<button class="pcard__size" type="submit" name="id" value="' +
            escapeHtml(variant.id) +
            '"' +
            (variant.available ? '' : ' disabled data-unavailable') +
            ' data-quick-submit>' +
            escapeHtml(variant.title) +
            '<span class="visually-hidden">' +
            (variant.available ? ', add to cart' : ', sold out') +
            '</span></button>'
          );
        })
        .join('');

      return (
        '<kosha-quick-add class="pcard__quick" data-handle="' +
        escapeHtml(data.handle) +
        '">' +
        '<button class="pcard__quick-btn" type="button" data-quick-trigger aria-expanded="false" aria-controls="' +
        escapeHtml(panelId) +
        '"><span data-quick-label>Quick add</span>' +
        '<span class="visually-hidden">' +
        escapeHtml(data.title) +
        '</span></button>' +
        '<form class="pcard__sizes" id="' +
        escapeHtml(panelId) +
        '" action="' +
        escapeHtml(action) +
        '" method="post" data-quick-form data-quick-panel hidden>' +
        '<input type="hidden" name="quantity" value="1">' +
        '<p class="pcard__sizes-title" id="' +
        escapeHtml(panelId) +
        '-label">Select a size</p>' +
        '<div class="pcard__sizes-row" role="group" aria-labelledby="' +
        escapeHtml(panelId) +
        '-label">' +
        pills +
        '</div>' +
        '<button class="pcard__sizes-close" type="button" data-quick-close>' +
        '<span class="visually-hidden">Close size picker</span>' +
        '<svg class="kosha-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
        '<path d="m6 6 12 12M18 6 6 18" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>' +
        '</button></form></kosha-quick-add>'
      );
    }

    /* Two or more option groups — a set. Same panel machinery, one legend and
       one pill row per group, and the variant matrix rebuilt from the payload
       we already have in hand. This is what keeps the "+" working after a
       colour-dot swap between two sets. */
    var groups = options
      .map(function (option) {
        var name = String(option.name || '');
        var legend = name.split(' - ')[0].trim();
        var lower = name.toLowerCase();
        if (lower === 'size' || lower === 'title') legend = 'Size';
        var pills = (option.values || [])
          .map(function (value) {
            return (
              '<button class="pcard__size" type="button" value="' +
              escapeHtml(value) +
              '" aria-pressed="false" data-quick-option>' +
              escapeHtml(value) +
              '</button>'
            );
          })
          .join('');
        return (
          '<fieldset class="pcard__group" data-quick-group>' +
          '<legend class="pcard__group-legend">' +
          escapeHtml(legend) +
          '</legend><div class="pcard__sizes-row">' +
          pills +
          '</div></fieldset>'
        );
      })
      .join('');

    /* Inside a <script> element the only sequence that can break out is a
       literal "</script", so `<` is the one character that has to go. */
    var matrix = JSON.stringify(
      variants.map(function (variant) {
        return [variant.id, variant.options || [], variant.available ? 1 : 0];
      })
    ).replace(/</g, '\\u003c');

    return (
      '<kosha-quick-add class="pcard__quick" data-handle="' +
      escapeHtml(data.handle) +
      '">' +
      '<a class="pcard__quick-btn" href="' +
      escapeHtml(data.url) +
      '" data-quick-trigger aria-expanded="false" aria-controls="' +
      escapeHtml(panelId) +
      '"><span data-quick-label>Choose sizes</span>' +
      '<span class="visually-hidden">for ' +
      escapeHtml(data.title) +
      '</span></a>' +
      '<form class="pcard__sizes pcard__sizes--multi" id="' +
      escapeHtml(panelId) +
      '" action="' +
      escapeHtml(action) +
      '" method="post" data-quick-form data-quick-panel aria-label="Select sizes for ' +
      escapeHtml(data.title) +
      '" hidden>' +
      '<input type="hidden" name="quantity" value="1">' +
      groups +
      '<div class="pcard__sizes-foot">' +
      '<button class="pcard__sizes-add" type="submit" name="id" value="" data-quick-submit disabled>' +
      '<span data-quick-add-label>Select sizes</span></button>' +
      '<button class="pcard__sizes-close" type="button" data-quick-close>' +
      '<span class="visually-hidden">Close size picker</span>' +
      '<svg class="kosha-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">' +
      '<path d="m6 6 12 12M18 6 6 18" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>' +
      '</button></div>' +
      '<script type="application/json" data-quick-variants>' +
      matrix +
      '<\/script>' +
      '</form></kosha-quick-add>'
    );
  }

  function markDots(card, activeHandle) {
    var dots = card.querySelectorAll('.pcard__dot');
    Array.prototype.forEach.call(dots, function (dot) {
      if (dot.classList.contains('pcard__dot--more')) return;
      var on = handleFromHref(dot.getAttribute('href')) === activeHandle;
      /* `aria-current` only. The dots are real <a href> links — `aria-pressed`
         is defined on button-ish roles and is invalid on `link`, so it was
         being dropped by the accessibility tree at best and confusing it at
         worst. `aria-current="true"` is the correct expression of "this is the
         one you are looking at", it is what the ring is keyed to, and it is
         what the server already renders for the self dot. */
      if (on) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  function snapshotCard(card) {
    if (cardSnapshots.has(card)) return cardSnapshots.get(card);
    var snap = {
      html: card.innerHTML,
      className: card.className,
      handle: card.getAttribute('data-handle') || '',
      hasAlt: card.hasAttribute('data-has-alt'),
    };
    cardSnapshots.set(card, snap);
    return snap;
  }

  function restoreCard(card) {
    var snap = cardSnapshots.get(card);
    if (!snap) return;
    card.innerHTML = snap.html;
    card.className = snap.className;
    card.setAttribute('data-handle', snap.handle);
    if (snap.hasAlt) card.setAttribute('data-has-alt', '');
    else card.removeAttribute('data-has-alt');
    markDots(card, snap.handle);
  }

  function applyProduct(card, data, colorName) {
    var media = card.querySelector('.pcard__media');
    var mediaLink = card.querySelector('.pcard__media-link');
    if (!media || !mediaLink) return;

    var existing = card.querySelector('.pcard__img--primary');
    var sizes = existing ? existing.getAttribute('sizes') : DEFAULT_CARD_SIZES;

    card.setAttribute('data-handle', data.handle);
    card.classList.toggle('pcard--soldout', !data.available);
    mediaLink.setAttribute('href', data.url);

    /* ------------------------------------------------------------ images */
    var images = (data.media || []).filter(function (entry) {
      return entry.media_type === 'image';
    });
    if (!images.length && data.featured_image) {
      images = [{ src: data.featured_image, width: 0, height: 0 }];
    }

    var placeholder = mediaLink.querySelector('.pcard__img--placeholder');
    if (placeholder && images.length) placeholder.remove();

    var primary = card.querySelector('.pcard__img--primary');
    if (images.length) {
      if (!primary) {
        primary = makeCardImage('pcard__img--primary');
        mediaLink.insertBefore(primary, mediaLink.firstChild);
      }
      /* Eager: the shopper just asked for this colour, it is not a
         below-the-fold guess any more. */
      paintImage(primary, images[0], sizes || DEFAULT_CARD_SIZES, 'eager');
    } else if (primary) {
      primary.remove();
    }

    var alt = card.querySelector('.pcard__img--alt');
    if (images.length > 1) {
      if (!alt) {
        alt = makeCardImage('pcard__img--alt');
        mediaLink.appendChild(alt);
      }
      paintImage(alt, images[1], sizes || DEFAULT_CARD_SIZES, 'lazy');
      /* Finding 1's gate follows the swap: a sibling with one photograph must
         not fade its only photograph out on hover either. */
      card.setAttribute('data-has-alt', '');
    } else {
      if (alt) alt.remove();
      card.removeAttribute('data-has-alt');
    }

    /* ------------------------------------------------------------- price */
    var compare = data.compare_at_price_min;
    if (compare == null) compare = data.compare_at_price;
    var onSale = compare != null && Number(compare) > Number(data.price);

    var priceEl = card.querySelector('.pcard__price');
    if (priceEl) {
      priceEl.className = 'pcard__price' + (onSale ? ' pcard__price--sale' : '');
      var priceHtml =
        '<span class="visually-hidden">' +
        (onSale ? 'Sale price' : 'Price') +
        '</span><span class="pcard__price-now">' +
        (data.price_varies ? 'From ' : '') +
        escapeHtml(formatMoney(data.price)) +
        '</span>';
      if (onSale) {
        priceHtml +=
          '<span class="visually-hidden">Regular price</span>' +
          '<s class="pcard__price-was">' +
          escapeHtml(formatMoney(compare)) +
          '</s>';
      }
      priceEl.innerHTML = priceHtml;
    }

    /* ------------------------------------------------------------- badge */
    var oldBadge = media.querySelector('.pcard__badge');
    if (oldBadge) oldBadge.remove();
    var badgeText = '';
    var badgeKind = '';
    if (!data.available) {
      badgeText = 'Sold out';
      badgeKind = 'soldout';
    } else if (onSale) {
      /* Same integer truncation Liquid's `divided_by` does. */
      var percent = Math.floor(((compare - data.price) * 100) / compare);
      if (percent > 0) {
        badgeText = percent + '% off';
        badgeKind = 'sale';
      }
    }
    if (badgeText) {
      mediaLink.insertAdjacentHTML(
        'afterend',
        '<span class="pcard__badge pcard__badge--' +
          badgeKind +
          '">' +
          escapeHtml(badgeText) +
          '</span>'
      );
    }

    /* ------------------------------------------------- title and cutline */
    var parts = String(data.title).split(' - ');
    var name = parts[0].trim();
    var suffix = parts.length > 1 ? parts.slice(1).join(' - ').trim() : '';

    var link = card.querySelector('.pcard__link');
    if (link) {
      link.setAttribute('href', data.url);
      link.textContent = name;
      if (suffix) {
        link.insertAdjacentHTML(
          'beforeend',
          '<span class="visually-hidden">, ' + escapeHtml(suffix) + '</span>'
        );
      }
    }

    var cutText = String(colorName || suffix || '').trim();
    var cutline = card.querySelector('.pcard__cutline');
    var title = card.querySelector('.pcard__title');
    if (cutText) {
      if (!cutline && title) {
        cutline = document.createElement('p');
        cutline.className = 'pcard__cutline';
        title.insertAdjacentElement('afterend', cutline);
      }
      if (cutline) cutline.textContent = cutText;
    } else if (cutline) {
      cutline.remove();
    }

    /* --------------------------------------------------------- quick add */
    var oldQuick = media.querySelector('kosha-quick-add, .pcard__quick-btn');
    if (oldQuick) oldQuick.remove();
    if (!card.hasAttribute('data-no-quick-add')) {
      panelSeq += 1;
      var markup = quickAddMarkup(data, 'QuickAddSwap-' + panelSeq);
      if (markup) media.insertAdjacentHTML('beforeend', markup);
    }

    /* The overflow link belongs to whichever product is showing. */
    var more = card.querySelector('.pcard__dot--more');
    if (more) more.setAttribute('href', data.url);

    markDots(card, data.handle);
  }

  function swapCard(card, dot) {
    var handle = handleFromHref(dot.getAttribute('href'));
    if (!handle) return false;

    var snap = snapshotCard(card);
    var token = (cardTokens.get(card) || 0) + 1;
    cardTokens.set(card, token);

    /* Optimistic: the ring moves on the click, not on the response. */
    markDots(card, handle);

    if (handle === snap.handle) {
      card.removeAttribute('data-swapping');
      restoreCard(card);
      return true;
    }

    var colorName = dot.getAttribute('title') || '';
    card.setAttribute('data-swapping', '');

    fetchProduct(handle)
      .then(function (data) {
        /* A newer click on the same card wins; this response is stale. */
        if (cardTokens.get(card) !== token) return;
        applyProduct(card, data, colorName);
      })
      .catch(function () {
        if (cardTokens.get(card) !== token) return;
        /* The shopper asked for a colour and we could not produce it in
           place — give them the thing the link always promised. */
        window.location.href = dot.href;
      })
      .then(function () {
        if (cardTokens.get(card) === token) card.removeAttribute('data-swapping');
      });

    return true;
  }

  /* Mark every dot as a toggle: with this script running they ARE toggles, and
     that has to be true in the accessibility tree before the first click, not
     after it. Re-run after an AJAX results swap brings in fresh cards. */
  function markAllDots(scope) {
    (scope || document).querySelectorAll('[data-product-card]').forEach(function (card) {
      if (!card.querySelector('.pcard__dot')) return;
      markDots(card, card.getAttribute('data-handle') || '');
    });
  }

  function initCardSwatches() {
    markAllDots();

    document.addEventListener('click', function (event) {
      if (!isPlainClick(event)) return;
      var dot = closestFrom(event.target, '.pcard__dot');
      if (!dot || dot.classList.contains('pcard__dot--more')) return;
      var card = dot.closest('[data-product-card]');
      if (!card) return;
      if (swapCard(card, dot)) event.preventDefault();
    });
  }

  /* ==========================================================================
     Filter disclosures + sort form
     ========================================================================== */

  function facets() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-facet]'));
  }

  function closeFacets(except) {
    facets().forEach(function (facet) {
      if (facet !== except) facet.open = false;
    });
  }

  /* The Filters row's collapsed state lives here rather than on the element:
     an AJAX filter change replaces the toolbar's innerHTML, so the button and
     the row are both new nodes on the other side of it and would otherwise
     spring back open under the shopper. */
  var facetsCollapsed = false;

  /* The panel and its backdrop are HOISTED TO <body> when the toolbar binds.

     A `position: fixed` element is only reliably fixed to the viewport when no
     ancestor has made itself a containing block, and this one was living inside
     a relatively-positioned, z-indexed toolbar that sits inside a hero band —
     any transform, filter or containment introduced anywhere up that chain
     silently re-anchors it, which is how the sheet ended up hanging off the
     bottom of the screen. As a child of <body> there is nothing above it to
     get in the way.

     It is the same hoist the size-guide drawer does in product.js, for the same
     reason. The AJAX filter swap rebuilds the toolbar, so any previously
     hoisted pair is dropped first and the fresh nodes take their place. */
  function hoistFacets(toolbar) {
    var panel = toolbar.querySelector('[data-facets]');
    var overlay = toolbar.querySelector('[data-facets-overlay]');
    if (!panel) return;
    document.querySelectorAll('body > [data-facets], body > [data-facets-overlay]').forEach(
      function (stale) {
        stale.remove();
      }
    );
    if (overlay) document.body.appendChild(overlay);
    document.body.appendChild(panel);
  }

  function applyFacetsCollapsed(toolbar) {
    var button = toolbar.querySelector('[data-facets-toggle]');
    /* Looked up on the document, not the toolbar: the panel has been hoisted
       out of it. */
    var row = document.querySelector('[data-facets]');
    if (!button || !row) return;
    button.setAttribute('aria-expanded', facetsCollapsed ? 'false' : 'true');
    if (facetsCollapsed) {
      closeFacets(null);
      row.setAttribute('hidden', '');
    } else {
      row.removeAttribute('hidden');
    }
    /* The backdrop tracks the panel exactly. It is a sibling rather than a
       pseudo-element so it can be clicked, and it is driven from the one place
       that already knows whether the filters are open. */
    var overlay = document.querySelector('[data-facets-overlay]');
    if (overlay) overlay.toggleAttribute('hidden', facetsCollapsed);
  }

  /* The panel starts closed. It used to be an inline row, where open was the
     right default because it pushed nothing over; as a panel over the page,
     open on arrival would be a dialog nobody asked for. */
  facetsCollapsed = true;

  /* Per-element wiring. Re-run after every AJAX toolbar swap, because those
     nodes are new; the document-level listeners below are bound once. */
  function bindToolbar() {
    document.querySelectorAll('[data-toolbar]').forEach(function (toolbar) {
      /* Marks the bar as JS-driven, which is what reveals the Filters toggle in
         collection.css. Without a script the row is simply always open and the
         button never appears. */
      toolbar.setAttribute('data-js', 'true');
      hoistFacets(toolbar);
      applyFacetsCollapsed(toolbar);

      var button = toolbar.querySelector('[data-facets-toggle]');
      if (!button || button.koshaBound) return;
      button.koshaBound = true;
      button.addEventListener('click', function () {
        facetsCollapsed = !facetsCollapsed;
        applyFacetsCollapsed(toolbar);
      });

      /* Close on the backdrop, on the panel's own X, and on Escape — the three
         ways out of anything that covers the page. Each puts focus back on the
         toggle, so a keyboard does not lose its place. */
      var closePanel = function () {
        if (facetsCollapsed) return;
        facetsCollapsed = true;
        applyFacetsCollapsed(toolbar);
        button.focus({ preventScroll: true });
      };

      var overlay = document.querySelector('[data-facets-overlay]');
      if (overlay && !overlay.koshaBound) {
        overlay.koshaBound = true;
        overlay.addEventListener('click', closePanel);
      }

      var closeBtn = document.querySelector('[data-facets-close]');
      if (closeBtn && !closeBtn.koshaBound) {
        closeBtn.koshaBound = true;
        closeBtn.addEventListener('click', closePanel);
      }

      toolbar.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closePanel();
      });
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closePanel();
      });
    });

    facets().forEach(function (facet) {
      if (facet.koshaBound) return;
      facet.koshaBound = true;
      facet.addEventListener('toggle', function () {
        if (facet.open) closeFacets(facet);
      });
    });

    document.querySelectorAll('[data-sort-form]').forEach(function (form) {
      /* Marks the form as JS-driven, which is what hides the no-JS Apply
         button in collection.css — the button stays in the DOM for everyone
         whose JS never runs. */
      form.setAttribute('data-js', 'true');
      if (form.koshaBound) return;
      form.koshaBound = true;
      var select = form.querySelector('select');
      if (!select) return;
      select.addEventListener('change', function () {
        if (typeof applySort === 'function' && applySort(form)) return;
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
      });
    });
  }

  function initFacetListeners() {
    document.addEventListener('click', function (event) {
      var inside = closestFrom(event.target, '[data-facet]');
      if (!inside) closeFacets(null);

      var card = closestFrom(event.target, 'kosha-quick-add');
      if (!card && openQuickAdd) openQuickAdd.close();
    });

    /* Document-level Escape. The element's own handler covers the normal case
       (focus is inside the panel), this covers every other one — focus blown
       away by a click on the page, an extension stealing it, whatever. */
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;

      if (openQuickAdd) {
        openQuickAdd.close(true);
        return;
      }

      var open = facets().filter(function (facet) {
        return facet.open;
      });
      if (!open.length) return;
      var summary = open[0].querySelector('summary');
      closeFacets(null);
      if (summary) summary.focus({ preventScroll: true });
    });
  }

  /* ==========================================================================
     AJAX filtering / sorting / paging (collection template only)
     --------------------------------------------------------------------------
     Everything here is an enhancement over markup that already works: filter
     values and pagination are real <a> links, the sorter is a real GET form
     with a real Apply button. With JS off, or if a fetch fails, the browser
     navigates and gets exactly the page it would have got before.

     Four wrappers are swapped, never removed, so the count's aria-live region
     and the facets' positioning context survive a filter change:
       [data-toolbar]  [data-applied]  [data-count]  [data-results]

     SCROLL POLICY: applying a filter or a sort does not move the page — the
     shopper is looking at the pills they just used and the first row is right
     under them. The one exception is when the results have been scrolled off
     the top (deep in a long grid), where staying put would leave them staring
     at a viewport of products that all just changed underneath them; then, and
     always for pagination, the page eases to the top of the results.
     ========================================================================== */

  var applySort = null;

  function initAjaxFilters() {
    var root = document.querySelector('[data-collection-root][data-section-id]');
    if (!root || !window.fetch || !window.history || !window.DOMParser) return;

    var sectionId = root.getAttribute('data-section-id');
    var regions = ['[data-toolbar]', '[data-applied]', '[data-count]', '[data-results]'];
    var controller = null;
    var currentUrl = location.pathname + location.search;

    function sectionUrl(href) {
      var url = new URL(href, location.href);
      url.searchParams.set('section_id', sectionId);
      return url.pathname + url.search;
    }

    function setBusy(on) {
      ['[data-count]', '[data-results]'].forEach(function (selector) {
        var node = root.querySelector(selector);
        if (!node) return;
        if (on) node.setAttribute('data-busy', '');
        else node.removeAttribute('data-busy');
      });
      root.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    function openFacetIndex() {
      var all = root.querySelectorAll('[data-facet]');
      for (var i = 0; i < all.length; i += 1) {
        if (all[i].open) return i;
      }
      return -1;
    }

    function reopenFacet(index, focus) {
      if (index < 0) return;
      var all = root.querySelectorAll('[data-facet]');
      var facet = all[index];
      if (!facet) return;
      facet.open = true;
      if (!focus) return;
      /* The link that was clicked no longer exists, so focus would fall to
         <body>. Park it on the disclosure the shopper is working inside. */
      var summary = facet.querySelector('summary');
      if (summary) summary.focus({ preventScroll: true });
    }

    /* `--header-h` is declared on :root by assets/header.css (56px mobile,
       76px from 990px up) and is the offset every sticky element in the theme
       uses. The literal is only a floor for the impossible case of the header
       section not rendering. */
    function headerOffset() {
      var raw = getComputedStyle(document.documentElement).getPropertyValue(
        '--header-h'
      );
      return (parseInt(raw, 10) || 56) + 16;
    }

    function settleScroll(mode) {
      var anchor = root.querySelector('[data-count]') || root;
      var top = anchor.getBoundingClientRect().top;
      if (mode !== 'top' && top >= headerOffset()) return;
      window.scrollTo({
        top: window.pageYOffset + top - headerOffset(),
        behavior: reducedMotion() ? 'auto' : 'smooth',
      });
    }

    function render(href, options) {
      options = options || {};
      var url = new URL(href, location.href);
      var target = url.pathname + url.search;

      if (controller) controller.abort();
      controller = 'AbortController' in window ? new AbortController() : null;
      var mine = controller;
      var openIndex = openFacetIndex();

      setBusy(true);

      return fetch(sectionUrl(href), {
        signal: mine ? mine.signal : undefined,
        headers: { Accept: 'text/html' },
      })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var next = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-collection-root]');
          if (!next) throw new Error('no section root in response');

          regions.forEach(function (selector) {
            var to = root.querySelector(selector);
            var from = next.querySelector(selector);
            if (to && from) to.innerHTML = from.innerHTML;
          });

          setBusy(false);
          bindToolbar();
          markAllDots(root);
          reopenFacet(openIndex, options.focus);

          if (options.push !== false && target !== currentUrl) {
            history.pushState({ kosha: 'collection' }, '', target);
          }
          currentUrl = target;
          settleScroll(options.scroll);
          if (controller === mine) controller = null;
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          setBusy(false);
          /* Anything unexpected — network, a 500, markup we do not recognise —
             falls back to the navigation the link was always going to do. */
          location.assign(href);
        });
    }

    /* Sort. Serialised from the form itself so the hidden inputs carrying the
       active filters go along, exactly as the no-JS submit would. */
    applySort = function (form) {
      var url = new URL(form.getAttribute('action') || location.pathname, location.href);
      url.search = new URLSearchParams(new FormData(form)).toString();
      render(url.pathname + url.search, { scroll: 'keep' });
      return true;
    };

    var LINKS =
      '.facet__value, .facet__clear, .applied__chip, .applied__clear, ' +
      '.pagination__page, .pagination__step';

    root.addEventListener('click', function (event) {
      if (!isPlainClick(event)) return;
      var link = closestFrom(event.target, LINKS);
      if (!link || link.tagName !== 'A' || !link.getAttribute('href')) return;
      event.preventDefault();
      var paging =
        link.classList.contains('pagination__page') ||
        link.classList.contains('pagination__step');
      render(link.href, {
        scroll: paging ? 'top' : 'keep',
        focus: link.classList.contains('facet__value'),
      });
    });

    history.replaceState({ kosha: 'collection' }, '', currentUrl);

    window.addEventListener('popstate', function () {
      var target = location.pathname + location.search;
      /* Hash-only changes and entries belonging to another page are not ours. */
      if (target === currentUrl) return;
      render(target, { push: false, scroll: 'keep' });
    });
  }

  /* ==========================================================================
     Boot
     ========================================================================== */

  /* ==========================================================================
     Grid / list view

     Layout-only: `data-view` on the section root drives CSS, the card markup is
     identical in both views. The root is never replaced by the AJAX layer, and
     the switch itself lives outside every swap target, so a filter change
     cannot reset the shopper's choice. Persisted per browser, not per URL —
     "I read grids as lists" is a preference, not a page state.
     ========================================================================== */

  var VIEW_KEY = 'kosha:collection-view';

  function initViewSwitch() {
    var root = document.querySelector('[data-collection-root]');
    if (!root) return;

    /* Reveals the switch in collection.css. Without a script there is no other
       view to switch to, so the buttons are never shown. */
    root.setAttribute('data-js', 'true');

    function apply(view, persist) {
      var next = view === 'list' ? 'list' : 'grid';
      root.setAttribute('data-view', next);
      root.querySelectorAll('[data-view-set]').forEach(function (button) {
        button.setAttribute(
          'aria-pressed',
          button.getAttribute('data-view-set') === next ? 'true' : 'false'
        );
      });
      if (!persist) return;
      try {
        localStorage.setItem(VIEW_KEY, next);
      } catch (error) {
        /* Private mode, quota, a blocked origin — the view still works. */
      }
    }

    var saved = null;
    try {
      saved = localStorage.getItem(VIEW_KEY);
    } catch (error) {
      saved = null;
    }
    apply(saved || 'grid', false);

    root.addEventListener('click', function (event) {
      var button = closestFrom(event.target, '[data-view-set]');
      if (!button) return;
      apply(button.getAttribute('data-view-set'), true);
    });
  }

  function init() {
    bindToolbar();
    initViewSwitch();
    initFacetListeners();
    initCardSwatches();
    initAjaxFilters();
  }

  if (!window.customElements.get('kosha-quick-add')) {
    window.customElements.define('kosha-quick-add', KoshaQuickAdd);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
